import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Create custom icons
const greenIcon = new L.Icon({
  iconUrl: '/images/markers/marker-icon-2x-green.png',
  shadowUrl: '/images/markers/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const redIcon = new L.Icon({
  iconUrl: '/images/markers/marker-icon-2x-red.png',
  shadowUrl: '/images/markers/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const blueIcon = new L.Icon({
  iconUrl: '/images/markers/marker-icon-2x-blue.png',
  shadowUrl: '/images/markers/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  popupContent: React.ReactNode;
  type: 'default' | 'source' | 'destination';
}

interface Line {
  from: [number, number];
  to: [number, number];
}

interface BaseMapProps {
  markers: MapMarker[];
  lines?: Line[];
  center?: [number, number];
  zoom?: number;
  onMapReady?: (map: L.Map) => void;
  className?: string;
}

// Component to handle map updates
const MapUpdater: React.FC<{
  markers: MapMarker[];
  center?: [number, number];
  zoom?: number;
}> = ({ markers, center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (markers.length > 0) {
      const bounds = new L.LatLngBounds(
        markers.map(marker => [marker.latitude, marker.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (center && zoom) {
      map.setView(center, zoom);
    }
  }, [markers, center, zoom, map]);

  return null;
};

const BaseMap: React.FC<BaseMapProps> = ({
  markers,
  lines = [],
  center = [24.7136, 46.6753], // Default to Riyadh
  zoom = 6,
  onMapReady,
  className = "h-[500px]"
}) => {
  const getIcon = (type: MapMarker['type']) => {
    switch (type) {
      case 'source':
        return redIcon;
      case 'destination':
        return blueIcon;
      default:
        return greenIcon;
    }
  };

  return (
    <div className={className}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        ref={onMapReady}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapUpdater markers={markers} center={center} zoom={zoom} />
        
        {markers.map(marker => (
          <Marker
            key={marker.id}
            position={[marker.latitude, marker.longitude]}
            icon={getIcon(marker.type)}
          >
            <Popup>{marker.popupContent}</Popup>
          </Marker>
        ))}

        {lines.map((line, index) => (
          <Polyline
            key={index}
            positions={[line.from, line.to]}
            color="#16a34a"
            weight={2}
            opacity={0.5}
            dashArray="5,10"
          />
        ))}
      </MapContainer>
    </div>
  );
};

export default BaseMap;